// AB2: fire_caster_uses_item_type_trigger.php, called after non-failed armor bending and before counterspell_check.
// Discovery, stateful gates, carrier/status order and optional-trigger consent belong to the shared executor.
import { type JanusCtx, type Row, ecId, itemField, upper, handBack } from './context.js';
import { type TriggerCandidate, type TriggerEvent, collectTriggerCandidates, gateTriggerCandidate, layerListEntryUuid } from '../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../reactions/janus-ctx-trigger-readers.js';
import { triggerExecutorGap, triggerFamilyPreDraw, runTriggerFamily, triggerCall } from './trigger-executor.js';
import { spellCircles } from './spell-circle-scope.js';

const USES_ITEM_TYPE = 'triggers_when_caster_uses_item_type';

/** One token per template invocation, never a per-use ability-id dedup (later child casts are independent). */
export interface CasterUsesItemTypePlan {
  readonly owner: Row;
  readonly event: TriggerEvent;
  fired: boolean;
}

export function itemTypeTriggerGap(ctx: JanusCtx, candidate: TriggerCandidate, event?: TriggerEvent): string | null {
  // Wizard v9 Mastery port (2026-10-09): the circle predicate (if_circle_of_magic_cast / _not_cast — Echoform Stability, Protective Surge)
  // is judged in pass 1 against the event's spellCircles (predicateMatch); triggerExecutorGap names it only when the event carries none.
  // These resource/movement effects have no executor twin. Its dynamic-fold check does not cover remaining-distance layers.
  for (const key of ['change_teleport_remaining', 'change_speed_walking_remaining', 'change_speed_flying_remaining',
    'change_speed_swimming_remaining', 'change_speed_climbing_remaining', 'change_speed_burrowing_remaining',
    'change_speed_hovering_remaining', 'change_speed_jumping_remaining'])
    if (ctx.read.has(candidate.ability, key)) return `layer:${key}`;
  return triggerExecutorGap(ctx, candidate, event);
}

/** PRE-DRAW: preview the complete janus_trigger pool, including status authority and unresolved carrier references. */
export function planCasterUsesItemType(ctx: JanusCtx, ability: Row, caster: Row): CasterUsesItemTypePlan | null {
  const itemType = String(itemField(ability, 'item_type') ?? '');
  if (ctx.trigger || itemType === '') return null;
  const event: TriggerEvent = { trigger: USES_ITEM_TYPE, itemType, spellLevel: itemType === 'spell' ? Number(ctx.read.value(ability, 'spell_level') ?? 0) : null,
    // the original cast's circles: a spell's spell_circle ids, [] for any other item type (no circle was cast)
    spellCircles: itemType === 'spell' ? spellCircles(ctx.read, ability) : [] };
  triggerFamilyPreDraw(ctx, caster, event, itemTypeTriggerGap);
  // AB2-CUIT-13: the natural Pulse Barrier holder also has Split Feint. Outside combat PHP fires that CMRT sibling
  // both before the attack and again after it (no reaction debit), with an immediate roll_type_adv contribution.
  // The shared skill-roll CMRT port models next-roll STATUS grants, not this statusless double dispatch/bias shape.
  // Keep the independently proven combat path; hand this precise peace shape back before armor or attack dice.
  const rollType = upper(ctx.read.value(ability, 'roll_type'));
  if (rollType && !ctx.read.inCombat(ctx.encounter, caster)) {
    const read = janusCtxTriggerReaders(ctx);
    const siblingEvent: TriggerEvent = { trigger: 'triggers_when_caster_makes_roll_type', rollType };
    const { carriers, statuses } = triggerCall(() => collectTriggerCandidates(read, caster, siblingEvent));
    for (const sibling of [...carriers, ...statuses]) {
      const definition = sibling.ability;
      const bias = ['roll_type_adv', 'roll_type_dis'].some(key => ctx.read.entries(definition, key).some(v => upper(layerListEntryUuid(v)) === rollType));
      const statusWindow = ['duration_type', 'effect_ends_on_start_of_caster_next_turn', 'effect_ends_on_end_of_caster_next_turn',
        'effect_ends_on_target_start_of_turn', 'effect_ends_on_target_end_of_turn', 'persistent_aoe'].some(key => ctx.read.has(definition, key));
      if (bias && !statusWindow && triggerCall(() => gateTriggerCandidate(read, sibling, caster, siblingEvent, null)).fire)
        handBack('fire_caster_uses_item_type_trigger:peace-cmrt-immediate-roll-modifier');
    }
  }
  return { owner: caster, event, fired: false };
}

/** Call only after armor bending succeeds (or is inapplicable). A countered cast still used its item type. */
export function fireCasterUsesItemType(ctx: JanusCtx, plan: CasterUsesItemTypePlan | null): void {
  if (!plan || plan.fired || ctx.trigger || plan.event.itemType === '') return;
  plan.fired = true;
  // Recollect on current state: armor's surge may have changed carriers. PHP supplies self targets and NO inciting target.
  const owner = ctx.members.get(ecId(plan.owner)) ?? plan.owner;
  runTriggerFamily(ctx, plan.event, owner, null, [owner], false, itemTypeTriggerGap, true);
}
